export interface HanziSection {
  heading: string;
  body: string;
  image: string;
  keyword: string;
}

export interface PlotChapter {
  title: string;
  content: string;
}

export interface Movie {
  id: string;
  title: string;
  type: string;
  year: number;
  region: string;
  rating: number;
  heat: number;
  cover: string;
  director: string;
  actors: string[];
  releaseDate: string;
  tags: string[];
  synopsis: string;
  hanziTitle: string;
  hanziSummary: string;
  hanziSections: HanziSection[];
  plotIntro: string;
  plotChapters: PlotChapter[];
}

export const movies: Movie[] = [
  {
    id: 'm001',
    title: '青川旧梦',
    type: '电视剧',
    year: 2021,
    region: '中国大陆',
    rating: 8.6,
    heat: 9820,
    cover: 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20ink%20painting%20style%20movie%20poster%20with%20misty%20blue%20mountains%20and%20a%20lone%20figure%20in%20traditional%20robe%2C%20serene%20melancholic%20mood%2C%20deep%20indigo%20tones%2C%20elegant%20cinematic%20composition&width=600&height=850&seq=movie-cover-001&orientation=portrait',
    director: '沈知远',
    actors: ['程亦白', '苏见宁', '林夫人'],
    releaseDate: '2021-03-18',
    tags: ['古装', '悬疑', '文化'],
    synopsis: '一封残破的家书将流落江湖的旧日世家女子重新卷回青川镇的旧梦之中。她在一笔一画间辨认褪色的墨迹，也在墨迹背后读懂一个被埋葬的家族秘密。',
    hanziTitle: '「归」字何解：家书中的情感密码',
    hanziSummary: '剧中反复出现的手写家书是叙事核心，从「归」字的笔势与演变，看汉字如何承载思念与身份的确认。',
    hanziSections: [
      {
        heading: '字形溯源：甲骨文中的「归」',
        keyword: '归',
        body: '「归」在甲骨文中从「帚」从「止」，本义与清扫、返回相关。剧集用一个大特写镜头推到信纸上被泪痕晕开的「归」字，让观众在字形里读懂人物既渴望回归又深知无家可归的矛盾心理。',
        image: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20traditional%20Chinese%20calligraphy%20character%20written%20in%20ink%20on%20aged%20rice%20paper%20with%20a%20single%20tear%20stain%20smudge%2C%20warm%20soft%20lighting%2C%20intimate%20cinematic%20detail&width=1200&height=800&seq=hanzi-gu-detail-01&orientation=landscape',
      },
      {
        heading: '笔势里的情绪：一顿一提皆是心事',
        keyword: '顿笔',
        body: '书法指导为剧中不同角色设计了截然不同的书写习惯。沉稳有度的顿笔属于长辈，轻疾带怯的收笔属于年轻的逃亡者，一笔之差便区分了人物的处境与性格。',
        image: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20brush%20resting%20on%20an%20ink%20stone%20beside%20letter%20paper%2C%20dim%20interior%20shadow%2C%20moody%20chiaroscuro%20lighting%2C%20cultural%20documentary%20aesthetic&width=1200&height=800&seq=hanzi-gu-detail-02&orientation=landscape',
      },
      {
        heading: '文化寓意：字是家族的记忆载体',
        keyword: '家书',
        body: '剧中家书不仅是道具，更是推动情节的关键证据。汉字的稳定性让跨越数十年的信息得以传递，这也呼应了剧集「记忆不灭」的核心主题。',
        image: 'https://readdy.ai/api/search-image?query=Stack%20of%20old%20handwritten%20Chinese%20letters%20tied%20with%20red%20thread%20on%20a%20wooden%20table%2C%20warm%20amber%20window%20light%2C%20nostalgic%20still%20life&width=1200&height=800&seq=hanzi-gu-detail-03&orientation=landscape',
      },
    ],
    plotIntro: '全剧以三段时间线交错展开，围绕家书、身份与归来三个母题层层推进。',
    plotChapters: [
      {
        title: '第一章 · 雨夜归客',
        content: '开场一场暴雨中，女主携半封残信回到青川镇。镇上居民对她的态度暧昧，有人认出她，也有人刻意回避。',
      },
      {
        title: '第二章 · 墨迹辨伪',
        content: '女主在旧书房里比对字迹，发现家中流传的家书竟有后人补写的痕迹，一笔起势出卖了伪装者。',
      },
      {
        title: '第三章 · 山河旧梦',
        content: '真相揭开，所谓归来不过是一场跨越数十年的赎罪。女主最终选择把家书留在镇上的祠堂。',
      },
    ],
  },
  {
    id: 'm002',
    title: '霓裳羽衣',
    type: '电影',
    year: 2020,
    region: '中国大陆',
    rating: 8.2,
    heat: 8610,
    cover: 'https://readdy.ai/api/search-image?query=Elegant%20Tang%20dynasty%20court%20dance%20movie%20poster%20with%20flowing%20silk%20robes%20and%20warm%20orange%20lantern%20light%2C%20deep%20navy%20background%2C%20graceful%20cinematic%20art%20direction&width=600&height=850&seq=movie-cover-002&orientation=portrait',
    director: '周晚辞',
    actors: ['柳青禾', '裴照', '黎裳'],
    releaseDate: '2020-09-30',
    tags: ['历史', '歌舞', '爱情'],
    synopsis: '一位宫廷乐师与舞者因一支失传的乐曲相识，在盛世的华彩与倾覆之间，用音符与笔画留下彼此存在的证据。',
    hanziTitle: '「裳」与「衣」：一字之差见等级',
    hanziSummary: '影片服饰考据严谨，从「衣」与「裳」的分别出发，解析古代服饰汉字背后的礼制秩序。',
    hanziSections: [
      {
        heading: '上衣下裳：礼制的缩影',
        keyword: '裳',
        body: '古时「衣」指上装、「裳」指下裙，二者合称才成一套完整礼服。影片道具组在字幕与服饰标注上严格区分，让观众一眼看懂等级森严的着装规则。',
        image: 'https://readdy.ai/api/search-image?query=Detailed%20Tang%20dynasty%20silk%20garment%20display%20with%20golden%20embroidery%20on%20deep%20navy%20fabric%2C%20museum%20lighting%2C%20textile%20detail%20shot&width=1200&height=800&seq=hanzi-shang-01&orientation=landscape',
      },
      {
        heading: '偏旁里的世界：织物部首群',
        keyword: '纟',
        body: '「绫罗绸缎」皆从「纟」，电影用一组蒙太奇罗列不同织物的汉字，串联起丝绸之路的繁华图景。',
        image: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20woven%20silk%20threads%20in%20warm%20orange%20and%20navy%20tones%2C%20macro%20texture%20photography%2C%20soft%20directional%20light&width=1200&height=800&seq=hanzi-shang-02&orientation=landscape',
      },
    ],
    plotIntro: '影片以一支乐曲的失传与重现为主线，讲述华彩与遗憾交织的盛世故事。',
    plotChapters: [
      {
        title: '第一章 · 初闻仙乐',
        content: '乐师在宴席上奏出半阙失传旋律，舞者在台下不由自主地踏出舞步，两人命运就此交汇。',
      },
      {
        title: '第二章 · 倾国之色',
        content: '盛世的奢靡与隐忧同时铺展，一曲霓裳成为动荡来临前的绝唱。',
      },
      {
        title: '第三章 · 余音未散',
        content: '多年后，仅存的乐谱残页被人发现，故事在悠长的余音中落幕。',
      },
    ],
  },
  {
    id: 'm003',
    title: '碑文之下',
    type: '电影',
    year: 2022,
    region: '中国大陆',
    rating: 8.9,
    heat: 11240,
    cover: 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20stone%20stele%20with%20carved%20seal%20script%20in%20a%20misty%20courtyard%2C%20dark%20navy%20and%20grey%20palette%2C%20solemn%20archaeological%20cinematic%20mood&width=600&height=850&seq=movie-cover-003&orientation=portrait',
    director: '许观澜',
    actors: ['何砚舟', '沈寺', '柳明'],
    releaseDate: '2022-06-10',
    tags: ['考古', '悬疑', '历史'],
    synopsis: '一块出土碑文改写了地方志的记载，考古队员在拓片与考据之间，牵出一段被刻意抹去的往事。',
    hanziTitle: '碑文拓印：金石气里的汉字之美',
    hanziSummary: '从碑刻的刀痕与拓片的墨色，解读汉字在石刻中呈现出的力量感与庄重感。',
    hanziSections: [
      {
        heading: '金石气：刀与笔的对话',
        keyword: '碑',
        body: '碑刻汉字经过凿刻与风化，形成独特的「金石气」。影片用拓包拍打的近景声音设计，把汉字的质感从视觉延伸到听觉。',
        image: 'https://readdy.ai/api/search-image?query=Rubbing%20of%20ancient%20Chinese%20stone%20inscription%20with%20black%20ink%20on%20white%20paper%2C%20hands%20tamping%20with%20a%20pad%2C%20dark%20scholarly%20workshop%2C%20detailed%20texture&width=1200&height=800&seq=hanzi-bei-01&orientation=landscape',
      },
      {
        heading: '异体字：被时间改写的笔画',
        keyword: '异体',
        body: '碑文中出现的异体字成为破案关键。影片借专家的口吻讲解同一汉字在不同年代的多重写法，串联起断代的线索。',
        image: 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20seal%20script%20characters%20carved%20on%20grey%20stone%20surface%20macro%20shot%2C%20cold%20blue%20light%2C%20archaeological%20detail&width=1200&height=800&seq=hanzi-bei-02&orientation=landscape',
      },
    ],
    plotIntro: '故事围绕碑文真伪与地方记忆展开，情节紧凑，层层反转。',
    plotChapters: [
      {
        title: '第一章 · 破土',
        content: '施工队意外挖出石碑，考古队进驻现场，一块残碑让尘封的往事浮出水面。',
      },
      {
        title: '第二章 · 拓影',
        content: '拓片上的字迹与他人提供的证词矛盾，团队开始怀疑有人篡改历史。',
      },
      {
        title: '第三章 · 真相',
        content: '真相揭开时，人们发现被抹去的不是罪行，而是一段善意的谎言。',
      },
    ],
  },
  {
    id: 'm004',
    title: '风起洛阳城',
    type: '电视剧',
    year: 2021,
    region: '中国大陆',
    rating: 8.0,
    heat: 9310,
    cover: 'https://readdy.ai/api/search-image?query=Dramatic%20ancient%20Chinese%20city%20at%20night%20with%20lanterns%20and%20city%20walls%2C%20mysterious%20navy%20blue%20and%20amber%2C%20epic%20historical%20drama%20poster&width=600&height=850&seq=movie-cover-004&orientation=portrait',
    director: '陆长庚',
    actors: ['裴无咎', '云娘', '白十三'],
    releaseDate: '2021-11-05',
    tags: ['古装', '权谋', '悬疑'],
    synopsis: '洛阳城中一桩连环谜案，让三位素不相识的人被同一串暗号牵到一起，而暗号竟是藏在坊间门匾里的汉字。',
    hanziTitle: '门匾暗号：坊市汉字里的江湖',
    hanziSummary: '剧中以坊市门匾、招牌作为情报载体，解析商业用字如何被赋予隐秘含义。',
    hanziSections: [
      {
        heading: '招牌的玄机',
        keyword: '匾',
        body: '古代坊市的匾额字形、悬挂角度都可能承载信息。剧集通过角色「读匾」的推理过程，把汉字玩成了精妙的解谜工具。',
        image: 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20shop%20plaque%20with%20carved%20characters%20hanging%20above%20a%20night%20market%20street%2C%20warm%20lantern%20glow%20against%20deep%20navy%2C%20atmospheric&width=1200&height=800&seq=hanzi-bian-01&orientation=landscape',
      },
      {
        heading: '通假字：古人的谐音梗',
        keyword: '通假',
        body: '剧中人物利用通假与谐音传递密信，让观众在会心一笑中理解了古人借字表意的智慧。',
        image: 'https://readdy.ai/api/search-image?query=Hand%20writing%20coded%20Chinese%20characters%20on%20a%20paper%20slip%20by%20candlelight%2C%20mysterious%20warm%20tone%20in%20dark%20room%2C%20close%20up&width=1200&height=800&seq=hanzi-bian-02&orientation=landscape',
      },
    ],
    plotIntro: '三线并行，案中有案，最终指向一场更大的阴谋。',
    plotChapters: [
      {
        title: '第一章 · 灯下谜',
        content: '上元灯会之夜，一具尸体旁留下一块写有怪字的木牌，案件就此展开。',
      },
      {
        title: '第二章 · 匾中字',
        content: '三人发现全城的门匾首字连起来是一句话，顺着这条线索逐步逼近真相。',
      },
      {
        title: '第三章 · 城起风',
        content: '真相揭晓，幕后之人竟与多年前的一段旧案有关，洛阳城再起风云。',
      },
    ],
  },
  {
    id: 'm005',
    title: '墨迹山河',
    type: '电影',
    year: 2019,
    region: '中国大陆',
    rating: 8.4,
    heat: 7890,
    cover: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20ink%20landscape%20painting%20movie%20poster%20with%20mountains%20and%20rivers%2C%20monochrome%20ink%20with%20orange%20seal%20accent%2C%20refined%20artistic%20cinematic%20visual&width=600&height=850&seq=movie-cover-005&orientation=portrait',
    director: '林麓',
    actors: ['顾砚', '白清让'],
    releaseDate: '2019-05-20',
    tags: ['文艺', '历史', '书画'],
    synopsis: '一位修复古画的匠人，在一幅损毁的山水长卷里发现了前朝画师留下的隐秘题跋。',
    hanziTitle: '题跋：画中汉字的第二重叙事',
    hanziSummary: '画上的题跋与印章是理解作品的关键，汉字如何在画面之外补全一段历史。',
    hanziSections: [
      {
        heading: '题跋的位置即态度',
        keyword: '跋',
        body: '题跋写在画前为「题」，写在画后为「跋」。影片借修复师的手，讲解题跋位置所传递的归属与敬意。',
        image: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20ink%20landscape%20scroll%20painting%20with%20elegant%20calligraphy%20inscriptions%2C%20soft%20museum%20lighting%2C%20refined%20detail&width=1200&height=800&seq=hanzi-ba-01&orientation=landscape',
      },
      {
        heading: '印章里的姓名与心意',
        keyword: '印',
        body: '篆书印章方寸之间藏姓名与身份，修复师正是从一方小印认出了隐匿多年的作者。',
        image: 'https://readdy.ai/api/search-image?query=Red%20seal%20stamp%20on%20aged%20paper%20with%20carved%20seal%20script%2C%20ink%20stone%20and%20brush%20nearby%2C%20warm%20scholarly%20atmosphere%2C%20macro&width=1200&height=800&seq=hanzi-ba-02&orientation=landscape',
      },
    ],
    plotIntro: '影片节奏舒缓，以修复过程为线索，慢慢揭开画师身世。',
    plotChapters: [
      {
        title: '第一章 · 残卷',
        content: '修复师接到一幅损毁严重的山水长卷，画心已碎，只剩零散题跋。',
      },
      {
        title: '第二章 · 补笔',
        content: '在一次次补笔中，修复师逐渐读懂了画中隐藏的心事。',
      },
      {
        title: '第三章 · 山河在',
        content: '长卷复原之日，画师一生未言的心志终于被后人看见。',
      },
    ],
  },
  {
    id: 'm006',
    title: '描红',
    type: '电视剧',
    year: 2023,
    region: '中国大陆',
    rating: 8.7,
    heat: 10560,
    cover: 'https://readdy.ai/api/search-image?query=Warm%20nostalgic%20poster%20of%20a%20Chinese%20copybook%20with%20red%20character%20outlines%20and%20a%20brush%2C%20soft%20daylight%2C%20deep%20navy%20and%20orange%20accents%2C%20coming%20of%20age%20film%20aesthetic&width=600&height=850&seq=movie-cover-006&orientation=portrait',
    director: '温知夏',
    actors: ['小满', '陈老师', '阿禾'],
    releaseDate: '2023-04-12',
    tags: ['成长', '校园', '温情'],
    synopsis: '小城小学里，一支毛笔、一本描红簿，串起了三代人的师生情谊与一个关于坚持的温柔故事。',
    hanziTitle: '描红本：汉字启蒙里的耐心哲学',
    hanziSummary: '从「描红」这一传统启蒙方式，看汉字的笔顺与结构如何塑造人的心性。',
    hanziSections: [
      {
        heading: '一笔一画的规矩',
        keyword: '笔顺',
        body: '描红强调笔顺与间架结构，剧中用孩子反复练习的特写，讲透「先横后竖、先撇后捺」背后的秩序之美。',
        image: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20child%20practicing%20Chinese%20calligraphy%20on%20a%20red%20copybook%20with%20wooden%20desk%20and%20warm%20afternoon%20light%2C%20gentle%20nostalgic%20tone&width=1200&height=800&seq=hanzi-miao-01&orientation=landscape',
      },
      {
        heading: '间架结构：字的骨与气',
        keyword: '结构',
        body: '剧中老师常说「字要立得住」，讲的正是间架结构。汉字的平衡感，恰是人生的处世之道。',
        image: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20calligraphy%20practice%20sheets%20stacked%20on%20a%20desk%20with%20ink%20stone%2C%20soft%20warm%20light%2C%20clean%20composition&width=1200&height=800&seq=hanzi-miao-02&orientation=landscape',
      },
    ],
    plotIntro: '以一所小城小学为舞台，讲述平凡而动人的师生故事。',
    plotChapters: [
      {
        title: '第一章 · 新学期',
        content: '一位年轻的书法老师来到小城小学，接手了全校最「难带」的班级。',
      },
      {
        title: '第二章 · 一支笔',
        content: '一个总也写不好字的孩子，在老师的耐心下慢慢找到了自己的节奏。',
      },
      {
        title: '第三章 · 桃李',
        content: '多年后，当年写字最慢的孩子成为了老师，描红簿在新的手上一页页翻过。',
      },
    ],
  },
  {
    id: 'm007',
    title: '字里行间',
    type: '电影',
    year: 2018,
    region: '中国台湾',
    rating: 8.1,
    heat: 6740,
    cover: 'https://readdy.ai/api/search-image?query=Minimalist%20literary%20movie%20poster%20with%20floating%20Chinese%20characters%20on%20a%20light%20grey%20paper%20texture%2C%20single%20orange%20character%20accent%2C%20quiet%20melancholic%20tone&width=600&height=850&seq=movie-cover-007&orientation=portrait',
    director: '吴念书',
    actors: ['林以文', '郑晓'],
    releaseDate: '2018-10-19',
    tags: ['文艺', '剧情', '文字'],
    synopsis: '一位校对员在繁复的文字世界里，遇见了另一个同样执着于「一个字都不放过」的灵魂。',
    hanziTitle: '校对的眼睛：繁体与简体之间',
    hanziSummary: '影片以校对工作为切口，探讨繁体与简体汉字各自的审美与文化脉络。',
    hanziSections: [
      {
        heading: '一简一繁，皆是汉字',
        keyword: '繁简',
        body: '影片没有评判优劣，而是借人物之口说：字符只是载体，愿意认真读字的人，才是文字真正的守护者。',
        image: 'https://readdy.ai/api/search-image?query=Two%20stacks%20of%20books%20with%20traditional%20and%20simplified%20Chinese%20text%20side%20by%20side%20on%20a%20desk%2C%20soft%20window%20light%2C%20literary%20still%20life&width=1200&height=800&seq=hanzi-fan-01&orientation=landscape',
      },
      {
        heading: '错字里的温度',
        keyword: '错字',
        body: '一个反复出现的「错字」，其实是某人刻意留下的暗号。汉字的多义性在这里成了叙事的温柔伏笔。',
        image: 'https://readdy.ai/api/search-image?query=Red%20pen%20marks%20correcting%20Chinese%20characters%20on%20a%20manuscript%20page%2C%20warm%20desk%20lamp%20light%2C%20editorial%20close%20up&width=1200&height=800&seq=hanzi-fan-02&orientation=landscape',
      },
    ],
    plotIntro: '故事细腻克制，围绕文字与人心展开。',
    plotChapters: [
      {
        title: '第一章 · 一份校样',
        content: '女主接到一份问题重重的书稿，逐字校对的习惯让她与作者产生了隔空对话。',
      },
      {
        title: '第二章 · 隔空遇见',
        content: '两人从未谋面，却通过书稿上的批注逐渐走近彼此。',
      },
      {
        title: '第三章 · 落笔为念',
        content: '书稿付印，这段以字会友的缘分，也在扉页的致谢里留下了印记。',
      },
    ],
  },
  {
    id: 'm008',
    title: '砚台迷局',
    type: '电视剧',
    year: 2022,
    region: '中国大陆',
    rating: 8.3,
    heat: 8120,
    cover: 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20inkstone%20and%20brush%20on%20a%20dark%20desk%20with%20mysterious%20light%2C%20deep%20navy%20and%20aged%20gold%20tones%2C%20period%20mystery%20drama%20poster&width=600&height=850&seq=movie-cover-008&orientation=portrait',
    director: '郑墨',
    actors: ['白砚生', '苏怀真', '韩九'],
    releaseDate: '2022-08-01',
    tags: ['古装', '悬疑', '推理'],
    synopsis: '一方传世砚台引出连环命案，文房四宝成了揭开真相的四把钥匙。',
    hanziTitle: '文房四宝：笔墨纸砚里的汉字江湖',
    hanziSummary: '从笔、墨、纸、砚四字的字形与功用出发，看文房用具如何成为叙事的线索。',
    hanziSections: [
      {
        heading: '砚：从「石」从「见」',
        keyword: '砚',
        body: '「砚」由「石」与「见」组成，剧中以此字拆解引出一句关键暗语，让道具与文字融为一体。',
        image: 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20inkstone%20with%20dark%20ink%20and%20a%20brush%2C%20dramatic%20side%20lighting%2C%20mysterious%20dark%20wood%20desk%2C%20macro%20detail&width=1200&height=800&seq=hanzi-yan-01&orientation=landscape',
      },
      {
        heading: '四宝成谜',
        keyword: '笔墨纸砚',
        body: '四件文房用具分别对应四段线索，剧集借汉字的拆解组合，构建了一套精巧的推理结构。',
        image: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20scholar%20stationery%20arrangement%20with%20brush%20ink%20paper%20and%20stone%2C%20warm%20amber%20candlelight%2C%20elegant%20composition&width=1200&height=800&seq=hanzi-yan-02&orientation=landscape',
      },
    ],
    plotIntro: '推理节奏明快，文字元素贯穿始终。',
    plotChapters: [
      {
        title: '第一章 · 一方砚台',
        content: '典当行里出现一方来路不明的古砚，接手者当晚离奇遇害。',
      },
      {
        title: '第二章 · 拆字成谜',
        content: '侦探发现线索藏在文房用具对应的汉字拆解之中，逐层解开谜团。',
      },
      {
        title: '第三章 · 墨尽人现',
        content: '真相揭开，一方砚台背后是一段关于守护的旧约。',
      },
    ],
  },
  {
    id: 'm009',
    title: '墨香镇',
    type: '电影',
    year: 2023,
    region: '中国大陆',
    rating: 7.9,
    heat: 5320,
    cover: 'https://readdy.ai/api/search-image?query=Peaceful%20ancient%20Chinese%20canal%20town%20with%20white%20walls%20and%20dark%20tiles%20at%20dusk%2C%20warm%20orange%20lanterns%2C%20deep%20blue%20sky%2C%20tranquil%20cinematic%20poster&width=600&height=850&seq=movie-cover-009&orientation=portrait',
    director: '陈砚秋',
    actors: ['阿墨', '老匠', '春雪'],
    releaseDate: '2023-01-15',
    tags: ['温情', '小镇', '非遗'],
    synopsis: '小镇最后一家制墨作坊面临关停，一位返乡青年决定用一场汉字灯会把墨香留住。',
    hanziTitle: '制墨与识字：工匠名字里的乾坤',
    hanziSummary: '传统制墨师的名字与堂号藏着汉字文化，影片用一场灯会唤醒小镇对文字的珍视。',
    hanziSections: [
      {
        heading: '堂号：一个字的招牌',
        keyword: '堂号',
        body: '老字号的堂号往往只用一两字概括家训。影片中「墨香」二字既是店名，也是一代匠人的坚守。',
        image: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20ink%20making%20workshop%20with%20wooden%20molds%20and%20black%20soot%2C%20warm%20dim%20lighting%2C%20craft%20documentary%20style&width=1200&height=800&seq=hanzi-mo-01&orientation=landscape',
      },
      {
        heading: '灯会：让汉字亮起来',
        keyword: '灯',
        body: '汉字灯会把一个字变成一个可以行走的光影，让古老的文字重新回到普通人的日常生活里。',
        image: 'https://readdy.ai/api/search-image?query=Chinese%20lantern%20festival%20at%20night%20with%20glowing%20character%20lanterns%20over%20a%20canal%2C%20warm%20orange%20reflections%20on%20water%2C%20festive%20atmosphere&width=1200&height=800&seq=hanzi-mo-02&orientation=landscape',
      },
    ],
    plotIntro: '一部温柔的小镇题材电影，讲述守与变。',
    plotChapters: [
      {
        title: '第一章 · 回乡',
        content: '青年回到阔别多年的小镇，发现父亲的墨坊已难以为继。',
      },
      {
        title: '第二章 · 一场灯会',
        content: '他提议办一场汉字灯会，试图让小镇重新认识这门老手艺。',
      },
      {
        title: '第三章 · 墨香不散',
        content: '灯会大获成功，墨坊迎来了新的学徒，墨香在小镇延续。',
      },
    ],
  },
  {
    id: 'm010',
    title: '锦书难托',
    type: '电视剧',
    year: 2020,
    region: '中国香港',
    rating: 8.5,
    heat: 7640,
    cover: 'https://readdy.ai/api/search-image?query=Romantic%20period%20drama%20poster%20with%20lady%20in%20traditional%20dress%20by%20a%20window%20writing%20a%20letter%2C%20deep%20navy%20and%20warm%20amber%20tones%2C%20elegant%20melancholy&width=600&height=850&seq=movie-cover-010&orientation=portrait',
    director: '何婉之',
    actors: ['沈婉', '陆行远', '云姨'],
    releaseDate: '2020-02-14',
    tags: ['爱情', '年代', '书信'],
    synopsis: '战乱年代，一对恋人只能靠书信维系感情，一封信的误读让两人错失半生。',
    hanziTitle: '书信格式：见字如面的礼仪',
    hanziSummary: '从「见字如面」到落款称谓，解析传统书信里的汉字礼仪与情感重量。',
    hanziSections: [
      {
        heading: '见字如面：开头的深情',
        keyword: '见字如面',
        body: '「见字如面」四字道尽书信的温度。剧中人物每次提笔都先写这四个字，成为贯穿全剧的情感符号。',
        image: 'https://readdy.ai/api/search-image?query=Handwritten%20Chinese%20letter%20with%20brush%20on%20aged%20paper%20by%20a%20window%20with%20soft%20rain%2C%20warm%20nostalgic%20lighting%2C%20romantic%20period%20detail&width=1200&height=800&seq=hanzi-shu-01&orientation=landscape',
      },
      {
        heading: '称谓里的距离与亲密',
        keyword: '称谓',
        body: '同一封信里，称谓的变化暗示着两人关系的流转。汉字在这里成了最含蓄的情感表达。',
        image: 'https://readdy.ai/api/search-image?query=Stack%20of%20old%20love%20letters%20tied%20with%20ribbon%20and%20a%20dried%20flower%2C%20warm%20sepia%20tones%2C%20tender%20still%20life&width=1200&height=800&seq=hanzi-shu-02&orientation=landscape',
      },
    ],
    plotIntro: '一段跨越数十年的错过与守候。',
    plotChapters: [
      {
        title: '第一章 · 初见',
        content: '两人在码头相识，约定日后以书信联系。',
      },
      {
        title: '第二章 · 误读',
        content: '一封信因战乱残缺，被误解成诀别，两人就此失散。',
      },
      {
        title: '第三章 · 重逢',
        content: '多年后真相大白，垂暮的两人终于读懂了那封信。',
      },
    ],
  },
];

export const getMovieById = (id: string): Movie | undefined =>
  movies.find((movie) => movie.id === id);